/**
 * Geometry Rendering Constants
 *
 * Central definition of all visual properties for geometry rendering.
 * Eliminates magic numbers and ensures consistency across the application.
 *
 * ⚠️ IMPORTANT: All geometry visual properties MUST be defined here.
 * NO magic numbers or hardcoded values in components!
 *
 * @module constants/geometryRendering
 */

// ===== COLOR CONSTANTS =====
// Based on ColorPickerCompact.tsx swatches (line 49)
// These are the default colors used when user doesn't specify custom colors

/** Default marker color (blue) */
export const DEFAULT_MARKER_COLOR = '#3b82f6';

/** Default line color (red - matches ColorPickerCompact first red swatch) */
export const DEFAULT_LINE_COLOR = '#ef4444';

/** Default shape fill color (red) - also used for border */
export const DEFAULT_SHAPE_FILL_COLOR = '#ef4444';

// ===== STATUS HEX COLORS (WebGL) =====
// Concrete hex values for MapLibre Symbol Layer rendering.
// MapLibre WebGL cannot resolve CSS variables, so we use light-mode hex values.
// These correspond to the CSS variable values in global.css (--app-status-*).

/** Task status hex colors for WebGL rendering (light-mode values) */
export const STATUS_HEX_COLORS: Record<string, string> = {
  Pending: '#868e96',
  InProgress: '#4c6ef5',
  Delayed: '#fab005',
  Completed: '#40c057',
  Archived: '#adb5bd',
} as const;

/** Default fallback hex color for markers without custom or status color */
export const DEFAULT_MARKER_HEX_FALLBACK = '#6B7280';

/** Default marker shape when geometry has no explicit shape */
export const DEFAULT_MARKER_SHAPE = 'Circle';

/** Default marker icon when geometry has no explicit icon */
export const DEFAULT_MARKER_ICON = '';

// ===== LINE RENDERING CONSTANTS =====

/** Default line width in pixels */
export const LINE_DEFAULT_WIDTH = 3;

// ===== LINE JOIN/CAP CONSTANTS =====
// Line join and cap styles control how line segments connect and terminate
// Using 'round' prevents visible miter artifacts at acute angles (especially in triangles)

/**
 * Line join style for smooth corners at line intersections
 * 'round' creates rounded corners, preventing pointed miter artifacts at acute angles
 * Consistent with border/road layers (VoivodeshipBordersMapLibre, PolandBorderMapLibre)
 */
export const LINE_JOIN_STYLE = 'round' as const;

/**
 * Line cap style for smooth line endpoints
 * 'round' creates rounded line endings for professional appearance
 */
export const LINE_CAP_STYLE = 'round' as const;

/** Default line opacity (1.0 = fully opaque) */
export const LINE_DEFAULT_OPACITY = 1;

/** Additional width added to line when hovered (in pixels) */
export const LINE_HOVER_WIDTH_BOOST = 1.5;

/** Additional width added to hitbox layer for easier clicking/dragging (in pixels) */
export const LINE_HITBOX_WIDTH_BOOST = 10;

/** Additional width for line hitbox on touch devices (larger for easier finger targeting) */
export const LINE_HITBOX_WIDTH_BOOST_TOUCH = 16;

// ===== LINE INTERPOLATION CONSTANTS =====
// Constants for line type interpolation (Straight, Curved, Arc)

/** Minimum number of points required for interpolation to be applied */
export const MIN_POINTS_FOR_INTERPOLATION = 2;

/** Minimum number of points required to enable point connection (clicking existing point to connect) */
export const MIN_POINTS_TO_CONNECT = 2;

/** Number of interpolated points per segment for Catmull-Rom spline (Curved line type) */
export const CATMULL_ROM_SEGMENTS = 20;

// ===== GLOW EFFECT CONSTANTS =====
// Glow effect is rendered below the main line when hovered

/** Additional width for glow layer beyond main line width (in pixels) */
export const GLOW_WIDTH_BOOST = 6;

/** Opacity of glow effect (0.0-1.0, 0.25 = 25% transparent) */
export const GLOW_OPACITY = 0.25;

/** Blur amount for glow effect (higher = more diffuse) */
export const GLOW_BLUR_AMOUNT = 5;

// ===== SHAPE HOVER GLOW CONSTANTS =====
// Subtle glow effect for shapes when hovered (uses shape's own color)

/** Additional width for hover glow layer beyond shape border (in pixels) */
export const SHAPE_HOVER_GLOW_WIDTH = 1.5;

/** Opacity of hover glow effect (0.4 = subtle but visible) */
export const SHAPE_HOVER_GLOW_OPACITY = 0.4;

/** Blur amount for hover glow (3px = soft, subtle effect) */
export const SHAPE_HOVER_GLOW_BLUR = 3;

/**
 * Line offset for hover glow layer (calculated dynamically in component)
 * Formula: -(shapeBorderWidth / 2 + SHAPE_HOVER_GLOW_WIDTH / 2)
 * IMPORTANT: NEGATIVE value for polygons = outward positioning (MapLibre spec)
 * This positions the glow outside the border to ensure full visibility
 */

// ===== SHAPE SELECTION GLOW CONSTANTS =====
// Strong blue glow effect for shapes when selected in Selection mode

/** Color for selection glow (blue-6 accent color for consistency with Mantine theme) */
export const SHAPE_SELECTION_GLOW_COLOR = '#3b82f6';

/** Additional width for selection glow layer beyond shape border (in pixels) */
export const SHAPE_SELECTION_GLOW_WIDTH = 1;

/** Opacity of selection glow effect (0.85 = strong, intense visibility) */
export const SHAPE_SELECTION_GLOW_OPACITY = 0.95;

/** Blur amount for selection glow (4px = sharp, prominent effect) */
export const SHAPE_SELECTION_GLOW_BLUR = 3;

/**
 * Line offset for selection glow layer (calculated dynamically in component)
 * Formula: -(shapeBorderWidth / 2 + SHAPE_SELECTION_GLOW_WIDTH / 2)
 * IMPORTANT: NEGATIVE value for polygons = outward positioning (MapLibre spec)
 * This positions the glow outside the border to ensure full visibility
 */

// ===== BLUR CONSTANTS =====
// Blur is applied to geometry borders for visual effects

/** Blur amount when geometry is hovered */
export const BLUR_HOVERED = 1;

// ===== SHAPE RENDERING CONSTANTS =====

/** Default shape border width in pixels */
export const SHAPE_DEFAULT_BORDER_WIDTH = 3;

/** Default shape border opacity (1.0 = fully opaque) */
export const SHAPE_DEFAULT_BORDER_OPACITY = 1;

/** Shape fill opacity for Solid type (1.0 = 100% opaque, fully visible) */
export const SHAPE_FILL_OPACITY_SOLID = 1.0;

/** Shape fill opacity for SemiTransparent type (0.6 = 60% opaque, semi-transparent) */
export const SHAPE_FILL_OPACITY_SEMI_TRANSPARENT = 0.6;

/** Additional opacity boost when shape is hovered (added to base opacity, capped at 1.0) */
export const SHAPE_FILL_OPACITY_HOVER_BOOST = 0.2;

/** Additional width added to shape border when hovered (in pixels) */
export const SHAPE_BORDER_HOVER_WIDTH_BOOST = 1.0;

/** Additional width added to shape border when selected in Selection mode (in pixels, same as hover - glow provides distinction) */
export const SHAPE_BORDER_SELECTION_WIDTH_BOOST = 1.0;

// ===== SHAPE SIZE CONSTANTS =====

/** Default shape size when created (in pixels) - converted to degrees based on map scale */
export const SHAPE_DEFAULT_SIZE_PX = 80;

/**
 * Default shape size in geographic degrees (approximate for Poland latitudes at typical zoom)
 * Calculation: 1 degree ≈ 111km at equator, Poland latitude ~52°N
 * For 80px at 1920px screen width and ~800km visible width at zoom 10:
 * 80/1920 * 800km / 111km ≈ 0.003 degrees
 */
export const SHAPE_DEFAULT_SIZE_DEGREES = 0.003;

// ===== HANDLE RENDERING CONSTANTS =====
// Constants for line point handles and segment indicators

/** Point handle hitbox size (larger to cover number markers) */
export const POINT_HANDLE_HITBOX_SIZE = 20; // px

/** Point handle visual size (visible circle) */
export const POINT_HANDLE_VISUAL_SIZE = 12; // px

/** Point handle z-index (above number markers) */
export const POINT_HANDLE_Z_INDEX = 1001;

/** Point handle border width */
export const POINT_HANDLE_BORDER_WIDTH = 2; // px

/** Handle transition duration */
export const HANDLE_TRANSITION_DURATION = 0.2; // seconds

/** Handle hover scale multiplier */
export const HANDLE_HOVER_SCALE = 1.3;

/** Handle shadow opacity (normal state) */
export const HANDLE_SHADOW_OPACITY = 0.2;

/** Handle shadow opacity (hover state) */
export const HANDLE_SHADOW_HOVER_OPACITY = 0.3;

/** Segment indicator size */
export const SEGMENT_INDICATOR_SIZE = 8; // px

/** Segment indicator z-index (below point handles) */
export const SEGMENT_INDICATOR_Z_INDEX = 999;

/** Segment indicator border width */
export const SEGMENT_INDICATOR_BORDER_WIDTH = 1; // px

/** Segment indicator opacity (normal state) */
export const SEGMENT_INDICATOR_OPACITY = 0.6;

/** Segment indicator hover scale multiplier */
export const SEGMENT_HOVER_SCALE = 1.4;

// ===== SHAPE RESIZE HANDLE CONSTANTS =====
// Shape resize handles are larger than line point handles for better UX
// Users need to precisely grab corners/vertices when resizing shapes

/** Shape resize handle hitbox size (larger than line handles) */
export const SHAPE_HANDLE_HITBOX_SIZE = 24; // px (vs 20px for line handles)

/** Shape resize handle visual size (larger than line handles) */
export const SHAPE_HANDLE_VISUAL_SIZE = 16; // px (vs 12px for line handles)

// ===== HANDLE STATE CONSTANTS =====
// Visual sizing and scaling for different handle states

/** Point handle size when selected (applies to line vertices in Selection mode) */
export const POINT_HANDLE_SELECTED_SIZE = 16; // px

// ===== SELECTION MODE HANDLE CONSTANTS =====
// Larger sizes for easier interaction in Selection mode
// These values override defaults when activeTool === GeometryType.Selection

/** Point handle hitbox size in Selection mode (larger for easier clicking) */
export const POINT_HANDLE_HITBOX_SIZE_SELECTION = 32;

/** Point handle visual size in Selection mode (more visible) */
export const POINT_HANDLE_VISUAL_SIZE_SELECTION = 18;

/** Point handle selected size in Selection mode (prominent) */
export const POINT_HANDLE_SELECTED_SIZE_SELECTION = 22;

/** Point handle hitbox size in Selection mode on touch devices */
export const POINT_HANDLE_HITBOX_SIZE_SELECTION_TOUCH = 36;

/** Scale multiplier for selected handles */
export const HANDLE_SELECTED_SCALE = 1.15;

/** Scale multiplier for active/pressed handles */
export const HANDLE_ACTIVE_SCALE = 1.1;

/** Scale multiplier for delete button on hover */
export const DELETE_BUTTON_HOVER_SCALE = 1.1;

/** Scale multiplier for delete button when pressed */
export const DELETE_BUTTON_ACTIVE_SCALE = 0.95;

/** Initial scale for delete button fade-in animation */
export const DELETE_BUTTON_ANIMATION_SCALE = 0.8;

// ===== ACTIVE VERTEX CONSTANTS =====
// Visual styling for the active vertex during line drawing
// The active vertex is the point from which the next line segment will be drawn

/** Color for active vertex indicator (green to indicate "next connection point") */
export const ACTIVE_VERTEX_COLOR = '#10b981'; // green-500

/** Scale multiplier for active vertex (makes it larger than normal vertices) */
export const ACTIVE_VERTEX_SCALE = 1.1;

/** Duration of pulsing animation for active vertex (in seconds) */
export const ACTIVE_VERTEX_PULSE_DURATION = 2;

/** Minimum scale during pulse animation (slightly smaller) */
export const ACTIVE_VERTEX_PULSE_SCALE_MIN = 0.95;

/** Maximum scale during pulse animation (slightly larger) */
export const ACTIVE_VERTEX_PULSE_SCALE_MAX = 1.05;
